/**
 * Default operational riding positions derived from DCVFD roster data.
 * These serve as fallback defaults; active positions are fetched from the API
 * via /api/admin/positions at runtime (FR-ADMIN-02).
 *
 * Positions are grouped by apparatus type:
 *   Engine:    IO (Incident Officer), DPO (Driver/Pump Operator), LFF, FF, RFF (Recruit Firefighter)
 *   Ambulance: AIC (Attendant in Charge), DO (Driver Operator), Aide
 *   Medic:     AIC, DO, Aide
 *   Rescue:    IO, DO, LFF, FF, RFF
 *   Tower:     IO, AO (Aerial Operator), LFF, FF, RFF
 *   Brush:     IO (Incident Officer), DPO (Driver/Pump Operator), LFF (Lead Firefighter)
 *   Utility:   Operator, Aide
 */
export const POSITIONS = [
  { id: 'incident_officer', label: 'Incident Officer', abbr: 'IO' },
  { id: 'driver_pump_operator', label: 'Driver/Pump Operator', abbr: 'DPO' },
  { id: 'driver_operator', label: 'Driver Operator', abbr: 'DO' },
  { id: 'aerial_operator', label: 'Aerial Operator', abbr: 'AO' },
  { id: 'lead_firefighter', label: 'Lead Firefighter', abbr: 'LFF' },
  { id: 'firefighter', label: 'Firefighter', abbr: 'FF' },
  { id: 'recruit_firefighter', label: 'Recruit Firefighter', abbr: 'RFF' },
  { id: 'aic', label: 'Attendant in Charge', abbr: 'AIC' },
  { id: 'aide', label: 'Aide', abbr: 'Aide' },
  { id: 'operator', label: 'Operator', abbr: 'OP' },
  { id: 'battalion_chief', label: 'Battalion Chief', abbr: 'BC' },
  { id: 'safety_officer', label: 'Safety Officer', abbr: 'SAF' },
  { id: 'assistant_chief', label: 'Assistant Chief', abbr: 'AC' },
  { id: 'fire_chief', label: 'Fire Chief', abbr: 'C' },
  { id: 'fist', label: 'FIST (Support)', abbr: 'FIST' },
] as const;

export type PositionId = (typeof POSITIONS)[number]['id'];

export const POSITION_IDS = POSITIONS.map((p) => p.id) as unknown as readonly PositionId[];

export function isValidPosition(position: string): position is PositionId {
  return POSITION_IDS.includes(position as PositionId);
}

export function getPositionLabel(id: string): string {
  return POSITIONS.find((p) => p.id === id)?.label ?? id;
}

/**
 * Maps apparatus type → ordered list of valid position IDs for that unit type.
 */
export const POSITIONS_BY_UNIT_TYPE: Record<string, PositionId[]> = {
  engine: ['incident_officer', 'driver_pump_operator', 'lead_firefighter', 'firefighter', 'recruit_firefighter'],
  ambulance: ['aic', 'driver_operator', 'aide'],
  medic: ['aic', 'driver_operator', 'aide'],
  rescue: ['incident_officer', 'driver_operator', 'lead_firefighter', 'firefighter', 'recruit_firefighter'],
  tower: ['incident_officer', 'aerial_operator', 'lead_firefighter', 'firefighter', 'recruit_firefighter'],
  brush: ['incident_officer', 'driver_pump_operator', 'lead_firefighter'],
  utility: ['operator', 'aide'],
  buggy: ['battalion_chief', 'fist'],
  safety: ['safety_officer'],
  chief: ['fire_chief', 'fist'],
  assistant_chief: ['assistant_chief', 'fist'],
};

/** Get positions for a given apparatus type. Returns all positions if type is unknown. */
export function getPositionsForUnitType(unitType: string | null): (typeof POSITIONS)[number][] {
  if (!unitType) return [...POSITIONS];
  const ids = POSITIONS_BY_UNIT_TYPE[unitType];
  if (!ids) return [...POSITIONS];
  return ids.map((id) => POSITIONS.find((p) => p.id === id)!).filter(Boolean);
}
